import { useAppDispatch, useAppSelector } from '@/renderer/hooks/useRedux';
import {
  selectCanShiftSelectedOrderItems,
  selectDiningSectionsWithTables,
  selectIsOrderSplitModalOpen,
  selectIsRemovingOrderItem,
  selectIsShiftingOrderItem,
  selectSelectedOrder,
  selectSelectedOrderItems,
  shiftSelectedConfirmedOrderItems,
} from '@/renderer/store/runningOrder';
import { closeOrderSplitModal } from '@/renderer/store/runningOrder/runningOrderSlice';
import { OrderItemShiftModalSubmitPayload } from '@/renderer/types';
import { ArrowLeftRight, ArrowUpRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Button, Modal } from '../common';
import { OrderItemShiftModal } from './OrderItemShiftModal';
import { OrderItemWrapper } from './OrderItemWrapper';

export const SplitItemsModal = () => {
  const isOpen = useAppSelector(selectIsOrderSplitModalOpen);
  const dispatch = useAppDispatch();
  const orderItems = useAppSelector(selectSelectedOrderItems);
  const canShiftItems = useAppSelector(selectCanShiftSelectedOrderItems);
  const isRemovingOrderItem = useAppSelector(selectIsRemovingOrderItem);
  const isShiftingOrderItem = useAppSelector(selectIsShiftingOrderItem);
  const selectedOrder = useAppSelector(selectSelectedOrder);
  const diningSections = useAppSelector(selectDiningSectionsWithTables);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkShiftState, setBulkShiftState] = useState<{
    itemIds: string[];
    itemName: string;
    sourceTableId: number;
    sourceTableName: string;
  } | null>(null);
  const allItemsSelected = useMemo(
    () => selectedItems.size === orderItems.length,
    [selectedItems, orderItems],
  );

  const handleClose = () => {
    setSelectedItems(new Set());
    dispatch(closeOrderSplitModal());
  };

  const handleOnSelectItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleOpenBulkShiftModal = () => {
    if (!selectedOrder || selectedItems.size === 0) return;

    setBulkShiftState({
      itemIds: Array.from(selectedItems),
      itemName: `${selectedItems.size} items`,
      sourceTableId: selectedOrder.table_id,
      sourceTableName: selectedOrder.table_name,
    });
  };

  const handleSubmitBulkShift = async ({
    targetTableId,
  }: OrderItemShiftModalSubmitPayload) => {
    if (!bulkShiftState) return;

    const action = await dispatch(
      shiftSelectedConfirmedOrderItems({
        orderItemIds: bulkShiftState.itemIds,
        targetTableId,
      }),
    );

    if (shiftSelectedConfirmedOrderItems.fulfilled.match(action)) {
      setBulkShiftState(null);
      setSelectedItems(new Set());
      handleClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Split ${selectedItems.size > 0 ? selectedItems.size : ''} Items between tables`}
      size="lg"
    >
      <div className="flex flex-col gap-4 w-full">
        {canShiftItems && selectedItems.size > 0 ? (
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-xs font-semibold text-black/70">
              {selectedItems.size} item
              {selectedItems.size === 1 ? '' : 's'} selected
            </p>
            <Button
              type="button"
              className="h-8 px-3 text-xs font-semibold uppercase tracking-wide"
              onClick={handleOpenBulkShiftModal}
              disabled={
                isRemovingOrderItem || isShiftingOrderItem || allItemsSelected
              }
            >
              {allItemsSelected ? (
                <ArrowLeftRight size={14} />
              ) : (
                <ArrowUpRight size={14} />
              )}
              {allItemsSelected
                ? 'To move all items, use the SHIFT option'
                : 'Shift selected'}
            </Button>
          </div>
        ) : null}
        <div className="flex flex-col max-h-[50vh] space-y-3 overflow-y-auto">
          {orderItems.map((item) => (
            <OrderItemWrapper
              item={item}
              onSelect={handleOnSelectItem}
              isSelected={!!selectedItems.has(item.id)}
            />
          ))}
        </div>
      </div>
      {bulkShiftState ? (
        <OrderItemShiftModal
          isOpen={Boolean(bulkShiftState)}
          itemName={bulkShiftState.itemName}
          sourceTableId={bulkShiftState.sourceTableId}
          sourceTableName={bulkShiftState.sourceTableName}
          sections={diningSections}
          isSubmitting={isShiftingOrderItem}
          onClose={() => {
            if (isShiftingOrderItem) return;
            setBulkShiftState(null);
          }}
          onSubmit={handleSubmitBulkShift}
        />
      ) : null}
    </Modal>
  );
};
